import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Tag } from '../../types';
import { tagService } from '../../services';

interface TagPickerProps {
  /** Currently-selected tag IDs. */
  value: string[];
  /** Called whenever the selection changes. */
  onChange: (tagIds: string[]) => void;
  /** All tags available to pick from (e.g. owner's tag pool). */
  tags: Tag[];
  /** Refresh the parent's tag list after a new tag is created on the fly. */
  onTagsChanged?: () => void;
  label?: string;
  placeholder?: string;
}

/**
 * Multi-select dropdown with autocomplete + "create on the fly".
 *
 * Renders selected tags as inline pills above the input. Typing filters
 * the dropdown; if no exact match exists the dropdown shows a "Create
 * 'foo'" option that POSTs to /tags and selects the new tag.
 *
 * The dropdown is portalled to document.body and positioned absolutely
 * so it floats above modals/popups and never affects the parent layout
 * (no scrollbars in modals just because the dropdown opened).
 */
const TagPicker: React.FC<TagPickerProps> = ({
  value,
  onChange,
  tags,
  onTagsChanged,
  label,
  placeholder = 'Add tag...',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Reposition dropdown to anchor under the wrapper
  const updatePosition = useCallback(() => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    setDropdownPos({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  // Recompute on open + on scroll/resize so the dropdown tracks the input
  useEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true); // capture: catch nested scroll containers
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, updatePosition]);

  // Close on outside click — must check both wrapper and the portalled dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (wrapperRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setOpen(false);
      setQuery('');
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedTags = value
    .map(id => tags.find(t => t.id === id))
    .filter((t): t is Tag => !!t);

  const queryLower = query.trim().toLowerCase();
  const filteredOptions = tags.filter(t => {
    if (value.includes(t.id)) return false;
    if (!queryLower) return true;
    return t.name.toLowerCase().includes(queryLower);
  });

  const exactMatch = tags.find(
    t => t.name.toLowerCase() === queryLower,
  );
  const canCreate = queryLower.length > 0 && !exactMatch && !creating;

  const addTag = (tagId: string) => {
    if (!value.includes(tagId)) {
      onChange([...value, tagId]);
    }
    setQuery('');
    inputRef.current?.focus();
  };

  const removeTag = (tagId: string) => {
    onChange(value.filter(id => id !== tagId));
  };

  const handleCreate = async () => {
    const name = query.trim();
    if (!name) return;
    try {
      setCreating(true);
      const newTag = await tagService.create({ name });
      onTagsChanged?.();
      onChange([...value, newTag.id]);
      setQuery('');
      inputRef.current?.focus();
    } catch (err) {
      // Surface failures via the parent's tag list refresh; the picker
      // itself stays simple and doesn't render its own toasts.
      console.error('Failed to create tag', err);
    } finally {
      setCreating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredOptions.length > 0) {
        addTag(filteredOptions[0].id);
      } else if (canCreate) {
        handleCreate();
      }
    } else if (e.key === 'Backspace' && !query && selectedTags.length > 0) {
      removeTag(selectedTags[selectedTags.length - 1].id);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  };

  const showDropdown = open && (filteredOptions.length > 0 || canCreate) && dropdownPos;

  return (
    <div ref={wrapperRef} className="relative">
      {label && (
        <label className="block text-xs font-medium text-neutral-500 mb-1.5">
          {label}
        </label>
      )}

      {/* Selected pills + input, all in one bordered box */}
      <div
        onClick={() => {
          setOpen(true);
          inputRef.current?.focus();
        }}
        className="min-h-9 px-2 py-1 bg-surface border border-neutral-200 rounded-lg flex flex-wrap items-center gap-1.5 cursor-text"
      >
        {selectedTags.map(tag => (
          <span
            key={tag.id}
            className="inline-flex items-center gap-1 h-6 px-2 rounded-md text-xs font-medium border"
            style={
              tag.color
                ? {
                    backgroundColor: tag.color + '20',
                    borderColor: tag.color,
                    color: tag.color,
                  }
                : {
                    backgroundColor: 'var(--color-neutral-100)',
                    borderColor: 'var(--color-neutral-200)',
                    color: 'var(--color-neutral-700)',
                  }
            }
          >
            {tag.name}
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                removeTag(tag.id);
              }}
              className="opacity-60 hover:opacity-100"
              aria-label={`Remove ${tag.name}`}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={selectedTags.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[80px] h-6 px-1 bg-transparent text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none"
        />
      </div>

      {/* Dropdown — portalled to body so it floats above modals and never
          affects the parent's scroll layout. */}
      {showDropdown &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: 'fixed',
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: dropdownPos.width,
            }}
            className="z-[60] max-h-60 overflow-y-auto bg-surface border border-neutral-200 rounded-lg shadow-lg"
          >
            {filteredOptions.map(tag => (
              <button
                key={tag.id}
                type="button"
                onClick={() => addTag(tag.id)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
              >
                {tag.color && (
                  <span
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: tag.color }}
                  />
                )}
                <span className="truncate">{tag.name}</span>
              </button>
            ))}
            {canCreate && (
              <button
                type="button"
                onClick={handleCreate}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-primary-700 hover:bg-primary-50 transition-colors border-t border-neutral-100"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create &ldquo;{query.trim()}&rdquo;
              </button>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default TagPicker;
